import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api, { setAuthToken, clearAuth } from "../../config/api";

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
};

export const registerUser = createAsyncThunk(
  "auth/register",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/register", credentials);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({
        success: false,
        message: "An unexpected error occurred",
      });
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/login", credentials);

      // Store token in localStorage for mobile Safari compatibility
      if (response.data.success && response.data.token) {
        setAuthToken(response.data.token);
        // Also store user data for persistence
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({
        success: false,
        message: "An unexpected error occurred",
      });
    }
  }
);

// Thunk for logging out a user
export const logoutUser = createAsyncThunk("auth/logout", async () => {
  const response = await api.post("/api/auth/logout", {});

  // Clear localStorage on logout
  clearAuth();

  return response.data;
});

export const checkAuth = createAsyncThunk(
  "auth/checkauth",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/api/auth/check-auth", {
        // A failed session check should leave public pages accessible. The
        // interceptor must not redirect the homepage to the login route.
        skipAuthRedirect: true,
        headers: {
          "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
          Expires: "0",
        },
      });
      return response.data;
    } catch (error) {
      // Clear auth on failure
      clearAuth();
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({
        success: false,
        message: "Authentication check failed",
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    // Add a reducer to initialize auth from localStorage
    initializeAuth: (state) => {
      const token = localStorage.getItem("authToken");
      const user = localStorage.getItem("user");
      if (token && user) {
        try {
          state.user = JSON.parse(user);
          state.isAuthenticated = true;
        } catch (e) {
          clearAuth();
        }
      }
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // Handle registerUser
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.isLoading = false;
        // Don't auto-authenticate on register (user needs approval)
      })
      .addCase(registerUser.rejected, (state) => {
        state.isLoading = false;
      });

    // Handle loginUser
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.success ? action.payload.user : null;
        state.isAuthenticated = action.payload.success;
      })
      .addCase(loginUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        clearAuth();
      });

    // Handle checkAuth
    builder
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.success ? action.payload.user : null;
        state.isAuthenticated = action.payload.success;
        // Update stored user data if check was successful
        if (action.payload.success && action.payload.user) {
          localStorage.setItem("user", JSON.stringify(action.payload.user));
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        clearAuth();
      });

    // Handle logoutUser
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout API fails, clear local state
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        clearAuth();
      });
  },
});

export const { setUser, initializeAuth } = authSlice.actions;
export default authSlice.reducer;
